import ErrorMessage from "components/error-message";
import BeerCard from "components/beer-card";
import useIdResource from "hooks/use-id-resource";
import { ChangeEvent, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { RootState, StoreError, beers, results } from "store";
import { TastingEvent } from "store/reducers/events/reducer";
import {
    Container,
    CountHint,
    CircularProgress,
    EmptyMessage,
    ListWrap,
    SearchBar,
    SearchField,
} from './Beers.styles';
import {
    beerFromEventBeerName,
    beerIdFromName,
    sortBeers,
} from "store/reducers/beers/utils";
import { Beer, beersSelectors } from "store/reducers/beers/reducer";
import { Result, resultsSelectors } from "store/reducers/results/reducer";

export type BeersParams = {
    id: string
}

const Beers = () => {
    const { id } = useParams<BeersParams>();
    const [query, setQuery] = useState("");
    const event = useSelector<RootState, TastingEvent | undefined>(
        s => s?.events?.items?.find(i => i.id === id)
    );
    const eventError = useSelector<RootState, StoreError | undefined>(
        s => s?.events?.itemsError[id]
    );

    // useIdResource owns the load + selectors + showLoading derivation. The
    // staleness guard inside the id-resource reducer prevents the A → B → A
    // race that used to leave this screen stuck on the spinner, and the
    // spinner only shows while a fetch is in flight with nothing committed.
    const {
        item: loadedBeers,
        error,
        showLoading,
    } = useIdResource<Beer[]>({
        id,
        selectors: beersSelectors,
        load: useCallback((eventId: string) => beers.load(eventId), []),
    });

    const { item: tastingResults, error: resultsError } = useIdResource<Result>({
        id,
        selectors: resultsSelectors,
        load: useCallback((eventId: string) => results.loadItem(eventId), []),
    });
    // Published per-round results contain the bartender's correct beers;
    // the event's beer list only identifies which beers are taking part.
    const publishedOrder = tastingResults?.lastUpdated && !resultsError
        ? tastingResults.roundResults?.[0]?.roundResults ?? []
        : [];
    const orderRevealed = publishedOrder.some(round => beerIdFromName(round.correctBeer));

    const roundFor = (beer: Beer): number | undefined => {
        const round = publishedOrder.find(
            result => beerIdFromName(result.correctBeer) === beer.id
        );
        return round ? round.index + 1 : undefined;
    };

    const eventBeerOrder = event?.beers || [];
    // The event's beer names are usually already in memory (events slice),
    // so the list renders instantly from them and the tasting notes fill in
    // when the catalog docs commit. The spinner only shows when we have
    // nothing at all to draw.
    const displayLoading = showLoading && !eventBeerOrder.some(Boolean);
    const eventBeers = eventBeerOrder
        .map(eventBeer => {
            const beerId = beerIdFromName(eventBeer);
            const foundBeer = loadedBeers?.find(beer => beer.id === beerId);
            return foundBeer ?? beerFromEventBeerName(eventBeer);
        })
        .sort((a, b) =>
            (roundFor(a) ?? Number.MAX_SAFE_INTEGER) -
            (roundFor(b) ?? Number.MAX_SAFE_INTEGER) || sortBeers(a, b)
        );

    const filtered = eventBeers.filter(b =>
        b.displayName.toLowerCase().includes(query.toLowerCase())
    );

    const handleQueryChange = (e: ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
    };

    return (
        <Container id="beers">
            {displayLoading ?
                <CircularProgress />
                :
                <>
                    {(error ?? eventError) && (
                        <ErrorMessage error={(error ?? eventError)!} />
                    )}
                    <SearchBar>
                        <SearchField value={query} onChange={handleQueryChange} />
                    </SearchBar>
                    <CountHint>
                        {filtered.length} beer{filtered.length === 1 ? "" : "s"}
                        {orderRevealed && " · Tasting order revealed"}
                    </CountHint>
                    <ListWrap>
                        {filtered.length === 0 ? (
                            <EmptyMessage>
                                {query
                                    ? `No beers match "${query}".`
                                    : "No beers added."}
                            </EmptyMessage>
                        ) : (
                            filtered.map((beer) => (
                                <BeerCard key={beer.id} beer={beer} round={roundFor(beer)} />
                            ))
                        )}
                    </ListWrap>
                </>
            }
        </Container>
    );
}
export default Beers;
